import { Pagination, Tooltip, clsx } from "@mantine/core";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  useOfferSummaryCount,
  usePublishedOffers,
  useCancelledOffers,
} from "hooks/usePublishedOffer";
import { FC, useEffect, useState } from "react";
import {
  OfferPublished,
  OfferStatus,
  TermsOfService,
} from "../../graphql/__generated__/globalTypes";

import { NetworkStatus, useMutation } from "@apollo/client";
import config from "config";
import { updateUser } from "features/users";
import { updateTermsOfService } from "graphql/mutations/publish-offer";
import useNotification from "hooks/useNotification";
import { usePage } from "hooks/usePage";
import { useRealtimeConnection } from "hooks/useRealTimeConnection";
import { useDebounce } from "react-use";
import PageHeader from "../../components/layout/page-header";
import PageWrapper from "../../components/layout/page-wrapper";
import OfferCardGrid from "./components/PublishedOfferCardGrid";
import TermsAgreementModal from "./components/TermsAgreementModal";

const AVAILABLE_STATUSES = [
  "All",
  OfferStatus.Open,
  OfferStatus.Pending,
] as const;

type SubFilterStatus = (typeof AVAILABLE_STATUSES)[number];
type MainFilterStatus = "TODAY" | "MISSED" | "CANCELLED";

const getButtonClasses = (isActive: boolean) =>
  clsx(
    "relative -ml-px inline-flex items-center rounded-md px-3 py-2 text-sm font-semibold ring-1 ring-gray-300 ring-inset transition-colors first:rounded-r-none even:rounded-none last:rounded-l-none hover:bg-green-500 focus:z-10 disabled:cursor-not-allowed",
    isActive ? "bg-green-700 text-white" : "bg-white text-gray-700"
  );

const getStatusTooltip = (status: SubFilterStatus): string => {
  switch (status) {
    case "All":
      return "Show all offers regardless of their current status";
    case OfferStatus.Open:
      return "Show offers that have been published but don't have any accepted participation.";
    case OfferStatus.Pending:
      return "Show offers that have been published and have an accepted participation.";
    default:
      return "";
  }
};

const PublishedOffers: FC = () => {
  const { notify } = useNotification();
  const { user } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();

  const reinsurerId =
    user?.clientable?.__typename === "Reinsurer_representative"
      ? Number(user.clientable.reinsurer?.reinsurer_id)
      : undefined;

  usePage("Kava | Published Offers");
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [filterStatus, setFilterStatus] = useState<MainFilterStatus>("TODAY");
  const [todaySubFilter, setTodaySubFilter] = useState<SubFilterStatus>("All");
  const [missedSubFilter, setMissedSubFilter] =
    useState<SubFilterStatus>("All");

  const [updateTermsMutation] = useMutation(updateTermsOfService);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const urlParams = new URLSearchParams(window.location.search);
  const searchFromParams = urlParams.get("search");
  const hasAcceptedTerms = user?.terms_of_service === TermsOfService.Accepted;

  useEffect(() => {
    if (searchFromParams) {
      setSearchValue(searchFromParams);
      setDebouncedSearchValue(searchFromParams);
    }
  }, [searchFromParams]);

  useEffect(() => {
    if (!hasAcceptedTerms) {
      setShowTermsModal(true);
    }
  }, [hasAcceptedTerms]);

  useDebounce(
    () => {
      setDebouncedSearchValue(searchValue);
    },
    1000,
    [searchValue]
  );

  const getCurrentSubFilter = () =>
    filterStatus === "TODAY" ? todaySubFilter : missedSubFilter;

  const getOfferStatusParam = () => {
    const currentSubFilter = getCurrentSubFilter();
    return currentSubFilter === "All" ? "" : currentSubFilter;
  };

  const { publishedOffers, loading, refresh, error, networkStatus } =
    usePublishedOffers({
      OfferStatus: getOfferStatusParam(),
      searchTerm: debouncedSearchValue || undefined,
      filter_status: filterStatus === "CANCELLED" ? "TODAY" : filterStatus,
      limit: perPage,
      offset: (page - 1) * perPage,
    });

  const { cancelledOffers, loading: cancelledLoading, refresh: refreshCancelled, error: cancelledError, networkStatus: cancelledNetworkStatus } = useCancelledOffers({
    published_by: "PLACEIT",
  });

  const { offerSummaryCount, loading: summaryLoading } = useOfferSummaryCount(
    reinsurerId,
    debouncedSearchValue || undefined
  );

  const currentOffers = filterStatus === "CANCELLED" ? cancelledOffers : publishedOffers;
  const currentLoading = filterStatus === "CANCELLED" ? cancelledLoading : loading;
  const currentError = filterStatus === "CANCELLED" ? cancelledError : error;
  const currentNetworkStatus = filterStatus === "CANCELLED" ? cancelledNetworkStatus : networkStatus;
  const currentRefresh = filterStatus === "CANCELLED" ? refreshCancelled : refresh;

  const totalOffers = currentOffers?.length || 0;
  const from = totalOffers === 0 ? 0 : (page - 1) * perPage + 1;
  const to = Math.min(page * perPage, totalOffers);
  const lastPage = Math.ceil(totalOffers / perPage);

  const handleOfferUpdate = async (message: any) => {
    // console.log("handleOfferUpdate called with message:", message);
    // console.log("Message data:", message?.data);
    refresh();
    await notify("KAVA NOTIFICATION - PUBLISHED OFFERS", {
      body: message.data,
    });
  };

  const handleSubFilterChange = (status: SubFilterStatus) => {
    if (filterStatus === "TODAY") {
      setTodaySubFilter(status);
    } else {
      setMissedSubFilter(status);
    }
    setPage(1);
    setSelectedItems([]);
  };

  const handleMainFilterChange = (status: MainFilterStatus) => {
    setFilterStatus(status);
    setPage(1);
    setSelectedItems([]);
  };

  const handleAgreeToTerms = async () => {
    try {
      setIsSubmitting(true);
      const result = await updateTermsMutation({
        variables: {
          args: {
            user_id: user?.id || "",
            terms_of_service: TermsOfService.Accepted,
          },
        },
      });
      setIsSubmitting(false);

      if (result.data.updateTermsOfService) {
        dispatch(
          updateUser({
            terms_of_service: TermsOfService.Accepted,
          })
        );
      }
    } catch (error) {
      setIsSubmitting(false);
      console.error("Error updating terms:", error);
    }
  };

  const handleDeclineTerms = () => {
    setShowTermsModal(false);
    if (typeof window !== "undefined") {
      window.location.href = "/app/dashboard";
    }
  };

  const statusFilterButtons = (
    <span className="isolate inline-flex rounded-md shadow-xs">
      {AVAILABLE_STATUSES.map((status) => {
        const isActive = getCurrentSubFilter() === status;
        return (
          <Tooltip
            key={status}
            label={getStatusTooltip(status)}
            position="top"
            withArrow
            multiline
            width={220}
          >
            <button
              type="button"
              disabled={isActive}
              className={getButtonClasses(isActive)}
              onClick={() => handleSubFilterChange(status)}
            >
              {status}
            </button>
          </Tooltip>
        );
      })}
    </span>
  );

  const TermsModal = () => (
    <TermsAgreementModal
      isOpen={showTermsModal}
      isSubmitting={isSubmitting}
      onAgree={handleAgreeToTerms}
      onDecline={handleDeclineTerms}
    />
  );

  useRealtimeConnection({
    subscriptions: [
      {
        channel: config.realtime.channels.KAVA_NOTIFICATION,
        events: [
          config.realtime.events.BROKER_PUBLISHED_OFFER,
          config.realtime.events.BROKER_UNPUBLISH_OFFER,
          config.realtime.events.BROKER_THREAD_NOTIFICATION,
          config.realtime.events.REINSURER_INTEREST,
          config.realtime.events.REINSURER_REMOVE_INTEREST,
          config.realtime.events.BROKER_ACCEPTED_INTEREST,
          config.realtime.events.BROKER_REMOVE_REINSURER_INTEREST,
        ],
      },
      {
        channel: config.realtime.channels.CLOSED_BUSINESS,
        events: [config.realtime.events.CLOSED_BUSINESS],
      },
      {
        channel: config.realtime.channels.KEK_CHANNEL,
        events: [
          config.realtime.events.BROKER_PUBLISHED_OFFER,
          config.realtime.events.BROKER_UNPUBLISH_OFFER,
          config.realtime.events.BROKER_THREAD_NOTIFICATION,
          config.realtime.events.REINSURER_INTEREST,
          config.realtime.events.REINSURER_REMOVE_INTEREST,
          config.realtime.events.BROKER_ACCEPTED_INTEREST,
          config.realtime.events.BROKER_REMOVE_REINSURER_INTEREST,
        ],
        pusherInstanceId: "kek",
      },
      {
        channel: config.realtime.channels.SAMPLE_CHANNEL,
        events: [
          config.realtime.events.BROKER_PUBLISHED_OFFER,
          config.realtime.events.BROKER_UNPUBLISH_OFFER,
          config.realtime.events.BROKER_THREAD_NOTIFICATION,
          config.realtime.events.REINSURER_INTEREST,
          config.realtime.events.REINSURER_REMOVE_INTEREST,
          config.realtime.events.BROKER_ACCEPTED_INTEREST,
          config.realtime.events.BROKER_REMOVE_REINSURER_INTEREST,
        ],
        pusherInstanceId: "sample",
      },
    ],
    callback: handleOfferUpdate,
  });

  if (!hasAcceptedTerms) {
    return <>{showTermsModal && <TermsModal />}</>;
  }
  return (
    <PageWrapper>
      <PageHeader title="Published Offers" />

      <section className="mt-8" aria-labelledby="offers-heading">
        <OfferCardGrid
          offers={(currentOffers || []) as any[]}
          searchValue={searchValue}
          search={setSearchValue}
          selected={selectedItems}
          loading={[NetworkStatus.loading].includes(currentNetworkStatus)}
          error={currentError}
          refresh={currentRefresh}
          renderAction={filterStatus !== "CANCELLED" ? statusFilterButtons : null}
          filterStatus={filterStatus}
          onFilterStatusChange={handleMainFilterChange}
          subFilterStatus={getCurrentSubFilter()}
          summaryCount={offerSummaryCount}
          summaryLoading={summaryLoading}
        />
      </section>

      <section className="p-3 flex justify-between items-center mt-6">
        <span>
          Showing {from} to {to} of {totalOffers} items
        </span>
        {lastPage > 1 && (
          <Pagination
            total={lastPage}
            page={page}
            onChange={(page) => {
              setPage(page);
              setSelectedItems([]);
            }}
          />
        )}
      </section>
    </PageWrapper>
  );
};

export default PublishedOffers;
